import { ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";

/**
 * Sortable Table Header Component
 * 
 * Usage:
 * const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
 * 
 * <SortableHeader
 *   label="Name"
 *   sortKey="name"
 *   sortConfig={sortConfig}
 *   onSort={setSortConfig}
 * />
 */
export const SortableHeader = ({ label, sortKey, sortConfig, onSort, className = "" }) => {
  const isActive = sortConfig.key === sortKey;
  const isAsc = isActive && sortConfig.direction === 'asc';
  const isDesc = isActive && sortConfig.direction === 'desc';

  const handleClick = () => {
    let direction = 'asc';
    if (isActive) {
      if (sortConfig.direction === 'asc') {
        direction = 'desc';
      } else {
        // Reset - no sorting
        onSort({ key: null, direction: 'asc' });
        return;
      }
    }
    onSort({ key: sortKey, direction });
  };

  return (
    <th 
      className={`cursor-pointer select-none hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors ${className}`}
      onClick={handleClick}
      data-testid={`sort-${sortKey}`}
    >
      <div className="flex items-center gap-1.5">
        <span>{label}</span>
        <span className={`transition-opacity ${isActive ? 'opacity-100' : 'opacity-30'}`}>
          {isDesc ? (
            <ArrowDown size={14} className="text-blue-500" />
          ) : isAsc ? (
            <ArrowUp size={14} className="text-blue-500" />
          ) : (
            <ArrowUpDown size={14} />
          )}
        </span>
      </div>
    </th>
  );
};

/**
 * Sort utility function
 * 
 * Usage:
 * const sortedData = sortData(data, sortConfig);
 */
export const sortData = (data, sortConfig) => {
  if (!sortConfig.key) return data;

  return [...data].sort((a, b) => {
    let aVal = a[sortConfig.key];
    let bVal = b[sortConfig.key];

    // Handle null/undefined
    if (aVal == null) aVal = '';
    if (bVal == null) bVal = '';

    // Handle dates (ISO strings or Date objects)
    if (sortConfig.key.includes('datum') || sortConfig.key.includes('date') || sortConfig.key === 'created_at') {
      aVal = new Date(aVal).getTime() || 0;
      bVal = new Date(bVal).getTime() || 0;
    }
    // Handle numbers
    else if (typeof aVal === 'number' || !isNaN(parseFloat(aVal))) {
      aVal = parseFloat(aVal) || 0;
      bVal = parseFloat(bVal) || 0;
    }
    // Handle strings
    else if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }

    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });
};

export default SortableHeader;
